/**
 * Ground Layer Store - State for traffic, CCTV, and seismic sub-layers
 *
 * 2026-08-17 — added THREAT HEATMAP (roadmap #1) and SUN + SHADOW STUDY
 * (roadmap #2). Both default OFF: they change what the shipped globe looks
 * like, so the operator opts in rather than having the view change under them.
 */

import { createStore } from "solid-js/store";
import type { Camera, EarthquakeData, StyleMode, GroundSubLayer } from "./types.ts";

/** Attribution + status for the threat heatmap drape (roadmap #1). */
export interface ThreatMeta {
  /** Short glanceable status: "412 incidents", "no public data here", "off". */
  status: string;
  /** Attribution strings for whatever is actually being painted. */
  sources: string[];
  count: number;
}

/** Solar geometry readout for the sun + shadow study (roadmap #2). */
export interface SunMeta {
  /** Sun bearing, degrees clockwise from true north. */
  azimuth: number;
  /** Degrees above horizon; negative means the sun is down. */
  elevation: number;
  /** Bearing shadows are cast toward (azimuth + 180). */
  shadowBearing: number;
  /** Compass point for the sun bearing, e.g. "WSW". */
  compass: string;
  /** Shadow length of a 10 m object, metres. null when the sun is too low. */
  shadowLengthM: number | null;
  isUp: boolean;
  timeISO: string | null;
  /** Set if enabling shadow MAPS threw — surfaced, never swallowed. */
  shadowError?: string;
}

export interface GroundState {
  trafficEnabled: boolean;
  cctvEnabled: boolean;
  seismicEnabled: boolean;
  tacticalEnabled: boolean;
  dronesEnabled: boolean;
  /** roadmap #1 — public-data threat/crime heatmap drape. */
  threatHeatmapEnabled: boolean;
  /** roadmap #2a — solar geometry (safe: pure maths + 2 polylines). */
  sunStudyEnabled: boolean;
  /** roadmap #2b — real cast shadow maps (shader-compile risk, see layer). */
  sunShadowsEnabled: boolean;
  /** Minutes offset from now for the studied instant. Scrub to event time. */
  sunTimeOffsetMin: number;
  trafficStyle: StyleMode;
  cctvCameras: Camera[];
  earthquakes: EarthquakeData[];
  centerStageCameraId: string | null;
  trafficLoading: boolean;
  trafficError: string | null;
  cctvLoading: boolean;
  cctvTooHigh: boolean;
  threatMeta: ThreatMeta;
  sunMeta: SunMeta;
}

const [groundState, setGroundState] = createStore<GroundState>({
  trafficEnabled: true,
  cctvEnabled: true,
  seismicEnabled: false,
  tacticalEnabled: true,
  dronesEnabled: true,
  threatHeatmapEnabled: false,
  sunStudyEnabled: false,
  sunShadowsEnabled: false,
  sunTimeOffsetMin: 0,
  trafficStyle: "heatmap",
  cctvCameras: [],
  earthquakes: [],
  centerStageCameraId: null,
  trafficLoading: false,
  trafficError: null,
  cctvLoading: true,
  cctvTooHigh: false,
  threatMeta: { status: "off", sources: [], count: 0 },
  sunMeta: {
    azimuth: 0,
    elevation: 0,
    shadowBearing: 0,
    compass: "N",
    shadowLengthM: null,
    isUp: false,
    timeISO: null,
  },
});

const SUB_LAYER_KEY: Record<GroundSubLayer, keyof GroundState> = {
  traffic: "trafficEnabled",
  cctv: "cctvEnabled",
  seismic: "seismicEnabled",
  tactical: "tacticalEnabled",
  drones: "dronesEnabled",
  threat: "threatHeatmapEnabled",
  sun: "sunStudyEnabled",
  shadows: "sunShadowsEnabled",
};

export const toggleSubLayer = (name: GroundSubLayer) => setGroundState(SUB_LAYER_KEY[name] as any, (p: boolean) => !p);
export const setSubLayerEnabled = (name: GroundSubLayer, enabled: boolean) => setGroundState(SUB_LAYER_KEY[name] as any, enabled);
export const setTrafficStyle = (mode: StyleMode) => setGroundState("trafficStyle", mode);
export const setCameras = (cameras: Camera[]) => setGroundState("cctvCameras", cameras);
export const setEarthquakes = (earthquakes: EarthquakeData[]) => setGroundState("earthquakes", earthquakes);
export const setCenterStageCamera = (id: string | null) => setGroundState("centerStageCameraId", id);
export const setTrafficLoading = (v: boolean) => setGroundState("trafficLoading", v);
export const setTrafficError = (v: string | null) => setGroundState("trafficError", v);
export const setCctvLoading = (v: boolean) => setGroundState("cctvLoading", v);
export const setCctvTooHigh = (v: boolean) => setGroundState("cctvTooHigh", v);

/** Partial update of the threat heatmap status/attribution. */
export const setThreatMeta = (m: Partial<ThreatMeta>) =>
  setGroundState("threatMeta", (prev) => ({ ...prev, ...m }));

/** Partial update of the solar readout. */
export const setSunMeta = (m: Partial<SunMeta>) =>
  setGroundState("sunMeta", (prev) => ({ ...prev, ...m }));

/** Scrub the studied instant, in minutes relative to now. */
export const setSunTimeOffset = (mins: number) => setGroundState("sunTimeOffsetMin", mins);

export { groundState };
